import { convexClient } from "../config/convex";
import { api } from "../../convex/_generated/api";
import { User, UserRole } from "../types/user";
import { browserAuthService as authService } from "./browserAuth";
import { v4 as uuidv4 } from "uuid";
import { cacheService } from "./cacheService";

function mapUser(doc: {
  id: string;
  email: string;
  role: string;
  created_at: string;
  created_by?: string;
  suspended?: number;
}): User & { id: string } {
  return {
    id: doc.id,
    email: doc.email,
    role: doc.role as UserRole,
    createdAt: new Date(doc.created_at),
    createdBy: doc.created_by,
    suspended: (doc.suspended ?? 0) === 1,
  };
}

export const userService = {
  async createUser(
    email: string,
    password: string,
    role: UserRole,
    createdBy: string
  ): Promise<{ id: string }> {
    try {
      sessionStorage.setItem("criticalOperation", "createUser");
      const count = await convexClient.query(api.users.count, {});
      const isFirstUser = count === 0;
      if (!isFirstUser) {
        const admin = await convexClient.query(api.users.getByEmail, {
          email: createdBy,
        });
        if (!admin || admin.role !== "Admin")
          throw new Error("Solo los administradores pueden crear usuarios");
      }
      const id = uuidv4();
      const passwordHash = await authService.hashPassword(password);
      await convexClient.mutation(api.users.create, {
        id,
        email,
        role,
        password_hash: passwordHash,
        created_at: new Date().toISOString(),
        created_by: createdBy,
      });
      return { id };
    } catch (error) {
      console.error("Error al crear usuario:", error);
      throw new Error("Error al crear el usuario");
    } finally {
      sessionStorage.removeItem("criticalOperation");
    }
  },

  async getCurrentUser(): Promise<User | null> {
    return authService.getCurrentUser();
  },

  async getCurrentUserRole(): Promise<UserRole | null> {
    const userData = await this.getCurrentUser();
    return userData?.role ?? null;
  },

  async isAdmin(): Promise<boolean> {
    const role = await this.getCurrentUserRole();
    return role === "Admin";
  },

  async getAllUsers(): Promise<(User & { id: string })[]> {
    const cacheKey = "all_users";
    const cached = cacheService.get<(User & { id: string })[]>(cacheKey);
    if (cached) return cached;
    const docs = await convexClient.query(api.users.list, {});
    const users = docs.map(mapUser);
    cacheService.set(cacheKey, users, 2 * 60 * 1000);
    return users;
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      sessionStorage.setItem("criticalOperation", "updateUser");
      const currentUser = await authService.getCurrentUser();
      if (!currentUser?.email) throw new Error("No se pudo identificar al usuario actual");
      const admin = await convexClient.query(api.users.getByEmail, {
        email: currentUser.email,
      });
      if (!admin || admin.role !== "Admin")
        throw new Error("Solo los administradores pueden actualizar usuarios");
      const patch: { email?: string; role?: string; suspended?: number } = {};
      if (updates.email !== undefined) patch.email = updates.email;
      if (updates.role !== undefined) patch.role = updates.role;
      if (updates.suspended !== undefined) patch.suspended = updates.suspended ? 1 : 0;
      if (Object.keys(patch).length > 0)
        await convexClient.mutation(api.users.update, {
          id: userId,
          ...patch,
        });
      cacheService.delete("all_users");
      cacheService.delete("active_users_count");
    } catch (error) {
      throw error instanceof Error ? error : new Error("Error al actualizar el usuario");
    } finally {
      sessionStorage.removeItem("criticalOperation");
    }
  },

  async deleteUser(userId: string): Promise<void> {
    try {
      sessionStorage.setItem("criticalOperation", "deleteUser");
      const currentUser = await authService.getCurrentUser();
      if (!currentUser?.email) throw new Error("No se pudo identificar al usuario actual");
      const admin = await convexClient.query(api.users.getByEmail, {
        email: currentUser.email,
      });
      if (!admin || admin.role !== "Admin")
        throw new Error("Solo los administradores pueden eliminar usuarios");
      await convexClient.mutation(api.users.remove, { id: userId });
      cacheService.delete("all_users");
      cacheService.delete("active_users_count");
    } catch (error) {
      throw error instanceof Error ? error : new Error("Error al eliminar el usuario");
    } finally {
      sessionStorage.removeItem("criticalOperation");
    }
  },

  async toggleUserSuspension(userId: string, suspended: boolean): Promise<void> {
    try {
      sessionStorage.setItem("criticalOperation", "toggleUserSuspension");
      const currentUser = await authService.getCurrentUser();
      if (!currentUser?.email) throw new Error("No se pudo identificar al usuario actual");
      const admin = await convexClient.query(api.users.getByEmail, {
        email: currentUser.email,
      });
      if (!admin || admin.role !== "Admin")
        throw new Error("Solo los administradores pueden suspender usuarios");
      await convexClient.mutation(api.users.suspend, {
        id: userId,
        suspended: suspended ? 1 : 0,
      });
      cacheService.delete("all_users");
      cacheService.delete("active_users_count");
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error("Error al cambiar suspensión del usuario");
    } finally {
      sessionStorage.removeItem("criticalOperation");
    }
  },

  async changeUserPassword(userId: string, newPassword: string): Promise<void> {
    try {
      sessionStorage.setItem("criticalOperation", "changeUserPassword");
      const currentUser = await authService.getCurrentUser();
      if (!currentUser?.email) throw new Error("No se pudo identificar al usuario actual");
      const admin = await convexClient.query(api.users.getByEmail, {
        email: currentUser.email,
      });
      if (!admin || admin.role !== "Admin")
        throw new Error("Solo los administradores pueden cambiar contraseñas");
      const passwordHash = await authService.hashPassword(newPassword);
      await convexClient.mutation(api.users.setPasswordById, {
        id: userId,
        password_hash: passwordHash,
      });
    } catch (error) {
      throw error instanceof Error ? error : new Error("Error al cambiar la contraseña");
    } finally {
      sessionStorage.removeItem("criticalOperation");
    }
  },

  async getActiveUsersCount(): Promise<number> {
    const cacheKey = "active_users_count";
    const cached = cacheService.get<number>(cacheKey);
    if (cached != null) return cached;
    const count = await convexClient.query(api.users.countActive, {});
    cacheService.set(cacheKey, count, 5 * 60 * 1000);
    return count;
  },

  async logout(): Promise<void> {
    authService.logout();
  },

  async migrateUsersFromFirebase(_users: unknown[]): Promise<void> {
    console.warn("Migración desde Firebase deshabilitada; se usa Convex");
  },
};

export default userService;
